import { Box, Button, DatePicker, Popover, Stack, useDisclosure } from '@tf/ui';
import { fmt } from '@tf/utils';
import type { FC } from 'react';
import { useState } from 'react';

import { DateValue } from '@/components/shared/DateValue';

const DateSelector: FC<{
	date: string | undefined;
	isLoading: boolean;
	onSave: (newDate: string) => void;
	onCancel: () => void;
}> = ({ date, isLoading, onSave, onCancel }) => {
	const [selectedDate, setSelectedDate] = useState(date ? new Date(date) : new Date());

	const save = () => {
		onSave(fmt.toDate(selectedDate, { preset: 'date_struct_with_time' }));
	};

	return (
		<Stack gap="0.75rem">
			<DatePicker
				locale="en"
				defaultDate={selectedDate}
				value={selectedDate}
				onChange={(newDate) => {
					if (newDate) {
						setSelectedDate(newDate);
					}
				}}
			/>
			<Box sx={{ display: 'flex', gap: '.75rem' }}>
				<Button fullWidth variant="light" size="sm" onClick={onCancel}>
					Cancel
				</Button>
				<Button loading={isLoading} loaderProps={{ size: 14 }} fullWidth size="sm" onClick={save}>
					Save
				</Button>
			</Box>
		</Stack>
	);
};

export const DateEditor: FC<{
	date: string | undefined;
	isLoading: boolean;
	onSave: (newDate: string) => Promise<unknown>;
}> = ({ date, isLoading, onSave }) => {
	const popover = useDisclosure();

	const save = async (newDate: string) => {
		await onSave(newDate);
		popover.close();
	};

	return (
		<Popover withArrow opened={popover.isOpen} onClose={popover.close}>
			<Popover.Target>
				<DateValue value={date} onClick={popover.toggle} />
			</Popover.Target>

			<Popover.Dropdown>
				<DateSelector date={date} isLoading={isLoading} onSave={save} onCancel={popover.close} />
			</Popover.Dropdown>
		</Popover>
	);
};
